// composables/useCounselorStatus.js
//
// 상담사 상태 수신 방식을 두 가지로 운영한다.
//   - "polling" : 기존 방식. 10초마다 getCounselorStatusFromDB 로 전체 조회
//   - "sse"     : subscribeCounselorStatus 에 EventSource 로 붙어 변경분만 수신
//
// 방식은 빌드 시 환경변수 VUE_STATUS_MODE 로 결정된다 (.env.* 참고).
// 값이 없으면 기존 동작인 "polling" 으로 둔다.

let pollingTimer = null;
let eventSource = null;

export const useCounselorStatus = () => {
  const statusMap = useState("counselorStatusMap", () => ({}));

  const getMode = () => {
    const config = useRuntimeConfig();
    return config.public.statusMode === "sse" ? "sse" : "polling";
  };

  // 단건 즉시 조회. 전화 연결 직전처럼 최신값이 반드시 필요한 지점에서 쓴다.
  // SSE 모드에서도 그대로 동작한다 (구독과 별개로 DB 를 직접 읽는다).
  const fetchStatus = async () => {
    try {
      const config = useRuntimeConfig();
      const fullUrl = `${config.public.apiServer}/api/auth/getCounselorStatusFromDB`;

      const res = await fetch(fullUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        console.warn("상태 조회 HTTP 오류 (무시):", res.status);
        return;
      }

      const response = await res.json();

      if (!response || response.resCd !== "0000") {
        console.warn("상태 조회 실패 (무시):", response?.resCd);
        return;
      }

      response.result.cslStatusList.forEach((csl) => {
        statusMap.value[csl.extKey] = csl.svcStatus;
      });

    } catch (e) {
      console.warn("상태 조회 오류 (무시):", e.message);
    }
  };

  const startPolling = (interval = 10000) => {
    if (pollingTimer) return; // 중복 방지
    fetchStatus();            // 즉시 1회 실행
    pollingTimer = setInterval(fetchStatus, interval);
  };

  const stopPolling = () => {
    clearInterval(pollingTimer);
    pollingTimer = null;
  };

  const startSse = () => {
    if (eventSource) return; // 중복 방지

    const config = useRuntimeConfig();
    const fullUrl = `${config.public.apiServer}/api/auth/subscribeCounselorStatus`;

    eventSource = new EventSource(fullUrl, { withCredentials: true });

    // 연결 직후 1회. 전체 상태를 통째로 교체한다.
    eventSource.addEventListener("snapshot", (e) => {
      try {
        statusMap.value = JSON.parse(e.data);
      } catch (err) {
        console.warn("상태 스냅샷 파싱 오류 (무시):", err.message);
      }
    });

    // 이후로는 바뀐 상담사 1건씩만 내려온다.
    eventSource.addEventListener("status", (e) => {
      try {
        const { extKey, svcStatus } = JSON.parse(e.data);
        if (!extKey) return;

        if (svcStatus === null || svcStatus === undefined) {
          delete statusMap.value[extKey];
        } else {
          statusMap.value[extKey] = svcStatus;
        }
      } catch (err) {
        console.warn("상태 변경 파싱 오류 (무시):", err.message);
      }
    });

    // EventSource 는 끊기면 스스로 재연결하고 snapshot 을 다시 받는다. 별도 복구 처리 불필요.
    eventSource.onerror = () => {
      console.warn("상태 구독 연결 오류 (자동 재연결 대기)");
    };
  };

  const stopSse = () => {
    if (!eventSource) return;
    eventSource.close();
    eventSource = null;
  };

  const start = (interval = 10000) => {
    if (getMode() === "sse") {
      if (typeof EventSource === "undefined") {
        // 구형 브라우저 등 EventSource 미지원 환경
        console.warn("EventSource 미지원 - 폴링으로 대체");
        startPolling(interval);
        return;
      }
      startSse();
      return;
    }
    startPolling(interval);
  };

  const stop = () => {
    stopPolling();
    stopSse();
  };

  const getStatus = (extKey) => statusMap.value[extKey] ?? null;

  return { statusMap, start, stop, startPolling, stopPolling, getStatus, fetchStatus };
};

/*
<script setup>
const { getStatus } = useCounselorStatus();
</script>

<template>
  <span>{{ getStatus(csl.extKey) === 'A' ? '상담가능' : '상담중' }}</span>
</template>
*/
